import { HttpClient } from '@angular/common/http';
import { computed, Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { environment } from '@environment';
import { CodeListItem } from '@models/codelist';
import { shareReplay } from 'rxjs';

export const OWNER_STATUS_ID = 5;

@Injectable({ providedIn: 'root' })
export class CodeListsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiUrl;

  readonly genders = toSignal(
    this.http.get<CodeListItem[]>(`${this.baseUrl}/api/codelists/genders`).pipe(shareReplay(1)),
    { initialValue: [] as CodeListItem[] },
  );

  readonly statuses = toSignal(
    this.http.get<CodeListItem[]>(`${this.baseUrl}/api/codelists/statuses`).pipe(shareReplay(1)),
    { initialValue: [] as CodeListItem[] },
  );

  readonly customerStatuses = computed(() => this.statuses().filter((s) => s.id !== OWNER_STATUS_ID));

  readonly sites = toSignal(this.http.get<CodeListItem[]>(`${this.baseUrl}/api/codelists/sites`).pipe(shareReplay(1)), {
    initialValue: [] as CodeListItem[],
  });

  readonly countries = toSignal(
    this.http.get<CodeListItem[]>(`${this.baseUrl}/api/codelists/countries`).pipe(shareReplay(1)),
    { initialValue: [] as CodeListItem[] },
  );

  readonly phoneNumberTypes = toSignal(
    this.http.get<CodeListItem[]>(`${this.baseUrl}/api/codelists/phone-number-types`).pipe(shareReplay(1)),
    { initialValue: [] as CodeListItem[] },
  );

  readonly reservationStatuses = toSignal(
    this.http.get<CodeListItem[]>(`${this.baseUrl}/api/codelists/reservation-statuses`).pipe(shareReplay(1)),
    { initialValue: [] as CodeListItem[] },
  );

  readonly waters = toSignal(
    this.http.get<CodeListItem[]>(`${this.baseUrl}/api/codelists/waters`).pipe(shareReplay(1)),
    { initialValue: [] as CodeListItem[] },
  );

  readonly exclusives = toSignal(
    this.http.get<CodeListItem[]>(`${this.baseUrl}/api/codelists/exclusives`).pipe(shareReplay(1)),
    { initialValue: [] as CodeListItem[] },
  );

  readonly amenities = toSignal(
    this.http.get<CodeListItem[]>(`${this.baseUrl}/api/codelists/amenities`).pipe(shareReplay(1)),
    { initialValue: [] as CodeListItem[] },
  );

  readonly coTravelerTypes = toSignal(
    this.http.get<CodeListItem[]>(`${this.baseUrl}/api/codelists/co-traveler-types`).pipe(shareReplay(1)),
    { initialValue: [] as CodeListItem[] },
  );

  readonly periodTypes = toSignal(
    this.http.get<CodeListItem[]>(`${this.baseUrl}/api/codelists/period-types`).pipe(shareReplay(1)),
    { initialValue: [] as CodeListItem[] },
  );

  // Lookup maps — recomputed only when a codelist signal changes
  readonly siteMap = computed(() => new Map(this.sites().map((s) => [s.id, s.name])));
  readonly genderMap = computed(() => new Map(this.genders().map((g) => [g.id, g.name])));
  readonly statusMap = computed(() => new Map(this.statuses().map((s) => [s.id, s.name])));
  readonly countryMap = computed(() => new Map(this.countries().map((c) => [c.id, c.name])));
  readonly reservationStatusMap = computed(() => new Map(this.reservationStatuses().map((s) => [s.id, s.name])));
  readonly exclusiveMap = computed(() => new Map(this.exclusives().map((e) => [e.id, e.name])));
  readonly amenityMap = computed(() => new Map(this.amenities().map((a) => [a.id, a.name])));
}
